/**
 * Form-document model shared by the web builder, the mobile renderer, and the
 * golden-vector evaluator tests. The Go API carries a typed mirror of these
 * shapes; the stored definition JSON travels byte-identical between server and
 * clients, so wire names here are snake_case and must never drift.
 */

export const ITEM_TYPES = [
  "pass_fail",
  "boolean",
  "number",
  "text",
  "select_one",
  "select_multiple",
  "date",
  "datetime",
  "document",
  "signature",
  "instruction",
  "facility_ref",
] as const;

export type ItemType = (typeof ITEM_TYPES)[number];

// Closed read-binding mode set. prefill pre-populates an editable answer from a
// server-resolved source; display renders a read-only server-resolved value.
export const READ_MODES = ["prefill", "display"] as const;
export type ReadMode = (typeof READ_MODES)[number];

/**
 * The evaluators' third input: resolved read-source values keyed by
 * read-source key. A display read-bound item reads its value here, never from
 * the answers. Two sources: the live read context while a document is
 * answered, and `readsFromSnapshot` over the document once it is sealed.
 */
export type ReadValues = Readonly<Record<string, string>>;

// Each registry read key logic may read — the device's stable identity — and
// its logic type. A snapshotted display item reading one may answer a
// condition, and a condition may name one directly as its question: a read
// subject, whose operand member follows this type. Statuses, due dates and every
// facility registry scalar stay display-only, because a derived value can
// differ between the render and the submit. Twin of Go
// `formtemplate.LogicEligibleReadKeys`, pinned through the shared vocabulary
// fixture.
export const LOGIC_ELIGIBLE_READ_KEYS: Readonly<Record<string, ItemType>> = {
  "device.serial_number": "text",
  "device.manufacturer_label": "text",
  "device.model_label": "text",
  "device.size_inches": "number",
  "device.assembly_type": "select_one",
  "device.service_type": "text",
  "device.meter_number": "text",
  "device.location": "text",
};

// The read-source key families of the org's own custom fields
// (`facility.cf.<key>` / `device.cf.<key>`); twin of Go
// `formtemplate.CustomFieldKeyPrefixFacility` / `CustomFieldKeyPrefixDevice`.
export const CUSTOM_FIELD_READ_PREFIXES = ["facility.cf.", "device.cf."] as const;

// The key families whose every display read a condition may reference, and
// whose every key a condition may name as a read subject: the custom-field
// families. A custom field is a stored scalar the office sets, never derived
// per read, so the sealed value is the value the document was evaluated
// against.
export const LOGIC_ELIGIBLE_READ_PREFIXES = CUSTOM_FIELD_READ_PREFIXES;

// The registry keys a write_back item may name beside the custom-field
// families: the premise's two hazard facts, columns of its own record. Every
// other registry key is read-only. Twin of Go
// `formtemplate.NativeWriteBackKeys`, pinned through the shared vocabulary
// fixture.
export const NATIVE_WRITE_BACK_KEYS = [
  "facility.degree_of_hazard",
  "facility.auxiliary_water_present",
] as const;

// The prefill's reserved `responses` member: an object of strings keyed by the
// link ids of the definition's write-back items, each the raw read the item's
// answer was SEEDED from — the baseline that tells a tester's change from an
// untouched prefill. The prefill records it in the draft when it seeds the
// answer and never re-records a key the draft already holds, so a draft resumed
// after the record changed keeps the read its answer came from;
// `finalizeAnswers` seals the draft's entries for the enabled write-back items
// and drops every other. The leading "__" can never match the link_id grammar,
// so a reserved member never collides with an answer.
export const PREFILL_BASELINE_MEMBER = "__prefill_baseline";

// The condition reads' reserved `responses` member: an object of strings keyed
// by every read subject the definition's conditions name, each the read the
// document was evaluated against ("" when absent). The receipt refuses a
// document whose member lacks a named subject and merges it into the read
// snapshot. Twin of Go `formtemplate.ReadsMember`.
export const READS_MEMBER = "__reads";

// The `responses` keys that are not answers.
export const RESERVED_RESPONSE_MEMBERS = [PREFILL_BASELINE_MEMBER, READS_MEMBER] as const;

export interface LocalizedText {
  en: string;
  es?: string;
}

export interface Option {
  value: string;
  label: LocalizedText;
}

export const HEADER_ACCENTS = ["default", "teal", "blue", "amber", "red", "gray"] as const;
export type HeaderAccent = (typeof HEADER_ACCENTS)[number];

export const FORM_THEMES = ["precision", "warm", "depth", "compact"] as const;
export type FormTheme = (typeof FORM_THEMES)[number];

// Closed section glyph vocabulary. `SECTION_ICON_PATHS` in `./section-icons.ts`
// is the one token→SVG map, keyed exhaustively by this tuple and in its order;
// each app supplies only the SVG element that draws a path from it.
export const SECTION_ICONS = [
  "clipboard",
  "gear",
  "droplet",
  "flame",
  "shield",
  "signature",
  "camera",
  "document",
  "wrench",
  "gauge",
  "beaker",
  "building",
  "contact",
  "valve",
  "meter",
  "hazard",
  "well",
  "pipe",
  "checklist",
  "note",
] as const;
export type SectionIconToken = (typeof SECTION_ICONS)[number];

// Closed instruction-block presentation set; "plain" is the default when
// absent.
export const INSTRUCTION_VARIANTS = ["plain", "info", "warning", "tip"] as const;
export type InstructionVariant = (typeof INSTRUCTION_VARIANTS)[number];

export interface FormPresentation {
  theme?: FormTheme;
}

export interface ItemDecoration {
  callout?: boolean;
}
export interface SectionDecoration {
  header_accent?: HeaderAccent;
  icon?: SectionIconToken;
}
export interface SectionLayout {
  columns?: 1 | 2 | 3 | 4;
}

export interface SectionSeed {
  read_source?: string; // a closed collection registry key, e.g. "facility.devices[]"
}
export interface RowEditing {
  allow_add?: boolean; // default true
  allow_edit?: boolean; // default true
  presentation?: "modal" | "inline"; // default "modal"
}

export type ConditionOp = "exists" | "eq" | "ne" | "gt" | "lt" | "ge" | "le";

/**
 * Single-key typed operand for a condition. Exactly one member is populated;
 * which one must match the referenced question's type (publish-gated).
 * select_one and select_multiple operands both use `option`.
 */
export interface TypedAnswer {
  pass_fail?: "pass" | "fail";
  boolean?: boolean;
  number?: number;
  text?: string;
  option?: string;
  date?: string;
  datetime?: string;
}

export interface Condition {
  question: string;
  op: ConditionOp;
  answer?: TypedAnswer;
}

/**
 * One-time client-side value mirror (evaluator-ignored). When the optional
 * `when` gate rises false→true, the carrying field copies `from`'s value once.
 * `when` is optional at the type level but publish-gate-required; `mode`
 * defaults "once". The pure evaluator never reads this — it exists only so the
 * stored definition round-trips byte-identically and the web composer / mobile
 * hook can apply the copy client-side.
 */
export interface CopyFrom {
  from: string;
  when?: Condition[];
  when_behavior?: "all" | "any";
  mode?: "once";
}

/** Closed computed-field operator vocabulary. */
export const COMPUTED_FIELD_OPS = [
  "add",
  "subtract",
  "multiply",
  "divide",
  "days_between",
] as const;
export type ComputedFieldOp = (typeof COMPUTED_FIELD_OPS)[number];

/**
 * A derived-value declaration on a number item: the item's value is
 * computed from exactly two earlier plain-input operands (linkIds),
 * rendered read-only, sealed into the submission document at finalize,
 * and recomputed server-side at receive (mismatch → 422). Arithmetic
 * ops take number operands and round half-to-even to `decimals`
 * (default 2, 0–6); days_between takes date/datetime operands and
 * yields a whole calendar-day count (first minus second; datetimes
 * reduce to their UTC calendar date), so `decimals` is forbidden on it.
 * Computed values stay terminal for enable_when, copy_from and other
 * computed items: none may reference one. Outcome conditions (group
 * criteria and `applies_when`) may, and read the value through the overlay
 * `outcomeGroups` builds, so a draft that carries no computed value is
 * judged on the value its seal will carry.
 */
export interface ItemComputed {
  op: ComputedFieldOp;
  operands: [string, string];
  decimals?: number;
}

export interface ItemValidation {
  required?: boolean;
  min?: number;
  max?: number;
  max_length?: number;
}

export interface FormItem {
  link_id: string;
  type: ItemType;
  unit?: string;
  label?: LocalizedText;
  options?: Option[];
  binding?: string;
  validation?: ItemValidation;
  enable_when?: Condition[];
  enable_behavior?: "all" | "any";
  read_only?: boolean;
  repeats?: boolean;
  // v2 additions (presentational; evaluator-ignored)
  placeholder?: LocalizedText;
  multiline?: boolean; // text only; default false (single-line)
  control?: "switch" | "checkbox"; // boolean only; default "switch"
  help_text?: LocalizedText;
  decoration?: ItemDecoration;
  // Grid span within the owning section's columns; "full" = every rendered
  // column. Publish-gated: span ≤ columns, "full" always valid.
  span?: 1 | 2 | 3 | 4 | "full";
  // instruction items only (publish-gated); default "plain".
  variant?: InstructionVariant;
  // v2 read-source binding: a closed read-source registry key (Go-owned) or
  // one of the org's custom-field keys (`facility.cf.<key>` /
  // `device.cf.<key>`, publish-gated against the org's definitions). A display
  // read of a logic-eligible key (the device identity keys and both
  // custom-field families) is a condition input; every other read is
  // presentation.
  read_source?: string;
  read_mode?: ReadMode; // prefill (editable) | display (read-only); default prefill
  read_snapshot?: boolean; // display only; default true — the seal writes the item's typed read into responses
  // prefill items reading a custom field or a NATIVE_WRITE_BACK_KEYS key only
  // (publish-gated): the accepted answer is written back to the record
  write_back?: boolean;
  // v2 copy-from mirror (evaluator-ignored): a one-time client-side value copy
  copy_from?: CopyFrom;
  // Derived-value declaration (number items only; publish-gated).
  computed?: ItemComputed;
}

export interface FormSection {
  link_id: string;
  title?: LocalizedText;
  // The item's two condition members: absent = enabled; a section is enabled
  // only under an enabled parent, and its items and sub-sections only inside it.
  enable_when?: Condition[];
  enable_behavior?: "all" | "any";
  // Optional author prose (1–3 sentences); evaluator-ignored. Publish-gated
  // by the same labels_missing rule as `title` (EN required when present; ES
  // required only on system-library rows).
  description?: LocalizedText;
  items: FormItem[];
  // v2 additions
  layout?: SectionLayout;
  decoration?: SectionDecoration;
  sections?: FormSection[]; // exactly one level (publish-gated in Go)
  repeats?: boolean;
  seed?: SectionSeed;
  row_editing?: RowEditing;
}

/**
 * One named group of pass criteria. The group is applicable when its
 * `applies_when` holds (absent or empty is true; `applies_behavior` defaults
 * to "all") and every item its criteria reference is enabled; an applicable
 * group passes when every criterion holds.
 */
export interface OutcomeGroup {
  key: string;
  label: LocalizedText;
  applies_when?: Condition[];
  applies_behavior?: "all" | "any";
  criteria: Condition[];
}

export interface AfterRepairEntry {
  initial: string;
  final: string;
}

/**
 * While the boolean `gate` item answers true, a criterion whose question is a
 * mapped `initial` reads the mapped `final` item instead. Applicability is
 * still judged on the authored reference.
 */
export interface AfterRepair {
  gate: string;
  map: AfterRepairEntry[];
}

/**
 * The derived-outcome block. `groups` is declared required, but a stored
 * pre-rule block lacks the member; every reader treats it as `[]`, which
 * evaluates as no block at all.
 */
export interface ComputedOutcome {
  target_link_id: string;
  groups: OutcomeGroup[];
  after_repair?: AfterRepair;
}

export type OutcomeGroupState = "pass" | "fail" | "not_applicable";

/** One group's derived state, in block order; derived on read, never stored. */
export interface OutcomeGroupResult {
  key: string;
  label: LocalizedText;
  state: OutcomeGroupState;
}

export interface Computed {
  outcome?: ComputedOutcome;
}

export interface FormDefinition {
  schema_version: number;
  target: "device" | "premise";
  sections: FormSection[];
  computed?: Computed;
  // Presentation-only, evaluator-ignored (publish-gated closed vocabulary).
  presentation?: FormPresentation;
}

/** Raw submission responses keyed by link_id; values are untrusted JSON. */
export type AnswerMap = Record<string, unknown>;

/** One row of a repeating section: child link_id → value. */
export type RowMap = Record<string, unknown>;
